import React, { useMemo, useState } from 'react';
import { useOpticalHistory } from '../hooks/useOpticalHistory.js';

/**
 * OpticalSparkline — chart SVG puro de rx_pwr e tx_pwr ao longo do tempo.
 *
 * MAP-33 — destrava resposta a "quando começou a degradação do rx desta ONU?"
 * em escala de dias. Dados de bi.fact_onu_optical_5m (AWS-12 + MAP-32),
 * resolução 1×/h + polls event-driven (MAP-37).
 *
 * Design rationale (pure SVG vs lib):
 *  - Caso de uso é simples (2 séries, ~24-2160 pontos, sem zoom/legenda).
 *  - Frontend ainda em CRA (memória frontend-deps-stack.md flag risk de
 *    package.json reorder em qualquer dep add).
 *  - recharts/@ant-design/charts traria 200-400KB pra um sparkline de ~80 LOC.
 *
 * Faixas de cor pro rx:
 *  - "safe"     (rx >  -20): verde
 *  - "warning"  (rx > -25): amarelo
 *  - "marginal" (rx ≤ -25): vermelho (mesmo threshold do índice parcial
 *                                       fact_onu_optical_5m_low_rx_idx na DDL)
 */

const RX_MARGINAL_THRESHOLD = -25; // dBm — mesmo do índice parcial na DDL
const RX_WARNING_THRESHOLD = -20;  // dBm

function rxColor(value) {
  if (value == null) return '#9ca3af';
  if (value <= RX_MARGINAL_THRESHOLD) return '#dc2626'; // red-600
  if (value <= RX_WARNING_THRESHOLD) return '#d97706';  // amber-600
  return '#16a34a'; // green-600
}

const DAY_OPTIONS = [
  { value: 1, label: '24h' },
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
];

const W = 320;
const H = 110;
const PAD_L = 36;
const PAD_R = 8;
const PAD_T = 8;
const PAD_B = 22;

function Empty({ message }) {
  return (
    <div
      style={{
        fontSize: 12,
        color: '#9ca3af',
        padding: '12px 8px',
        background: '#f9fafb',
        borderRadius: 4,
        textAlign: 'center',
      }}
    >
      {message}
    </div>
  );
}

export function OpticalSparkline({ sn }) {
  const [days, setDays] = useState(7);
  const { data: points = [], isLoading, error } = useOpticalHistory(sn, { days });

  const stats = useMemo(() => {
    if (!points.length) return null;
    const rx = points
      .map((p) => p.signal_rx_dbm)
      .filter((v) => v != null);
    const tx = points
      .map((p) => p.signal_tx_dbm)
      .filter((v) => v != null);
    return {
      n: points.length,
      rxLast: rx[rx.length - 1] ?? null,
      rxMin: rx.length ? Math.min(...rx) : null,
      rxMax: rx.length ? Math.max(...rx) : null,
      txLast: tx[tx.length - 1] ?? null,
      rxFirst: rx[0] ?? null,
    };
  }, [points]);

  const chart = useMemo(() => {
    if (!points.length) return null;
    const tMin = new Date(points[0].observed_at).getTime();
    const tMax = new Date(points[points.length - 1].observed_at).getTime();
    const tSpan = Math.max(tMax - tMin, 1);

    // Y range combina rx + tx pra mesmo eixo (range típico -33..10 dBm)
    const all = points.flatMap((p) => [p.signal_rx_dbm, p.signal_tx_dbm].filter((v) => v != null));
    if (!all.length) return null;
    let yMin = Math.floor(Math.min(...all) - 1);
    let yMax = Math.ceil(Math.max(...all) + 1);
    if (yMin === yMax) yMax = yMin + 1;
    const ySpan = yMax - yMin;

    const xScale = (t) => PAD_L + ((t - tMin) / tSpan) * (W - PAD_L - PAD_R);
    const yScale = (v) => PAD_T + (1 - (v - yMin) / ySpan) * (H - PAD_T - PAD_B);

    const pathFor = (key) =>
      points
        .map((p, i) => {
          const v = p[key];
          if (v == null) return null;
          const x = xScale(new Date(p.observed_at).getTime()).toFixed(1);
          const y = yScale(v).toFixed(1);
          return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
        })
        .filter(Boolean)
        .join(' ');

    // Linha de threshold marginal (-25 dBm) só se dentro do range visível
    const showThreshold = RX_MARGINAL_THRESHOLD >= yMin && RX_MARGINAL_THRESHOLD <= yMax;
    const yThreshold = showThreshold ? yScale(RX_MARGINAL_THRESHOLD) : null;

    // Ticks Y: 4 linhas equidistantes
    const yTicks = [];
    const yStep = ySpan / 3;
    for (let i = 0; i <= 3; i++) {
      const v = yMin + i * yStep;
      yTicks.push({ v: Math.round(v * 10) / 10, y: yScale(v) });
    }

    return {
      rxPath: pathFor('signal_rx_dbm'),
      txPath: pathFor('signal_tx_dbm'),
      xScale,
      yScale,
      yMin,
      yMax,
      yThreshold,
      yTicks,
      tMin,
      tMax,
    };
  }, [points]);

  return (
    <div style={{ marginTop: 4 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 6,
        }}
      >
        <div style={{ fontSize: 11, color: '#6b7280' }}>
          {stats && (
            <>
              rx{' '}
              <strong style={{ color: rxColor(stats.rxLast) }}>
                {stats.rxLast != null ? stats.rxLast.toFixed(2) : '—'}
              </strong>{' '}
              dBm
              {stats.rxMin != null && stats.rxMax != null && (
                <span style={{ color: '#9ca3af', marginLeft: 6 }}>
                  ({stats.rxMin.toFixed(2)} a {stats.rxMax.toFixed(2)})
                </span>
              )}
              {' · '}
              tx <strong>{stats.txLast != null ? stats.txLast.toFixed(2) : '—'}</strong> dBm
              <span style={{ color: '#9ca3af', marginLeft: 6 }}>· {stats.n} pontos</span>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {DAY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setDays(opt.value)}
              data-testid={`optical-range-${opt.value}d`}
              style={{
                fontSize: 11,
                padding: '2px 8px',
                border: '1px solid',
                borderColor: days === opt.value ? '#3b82f6' : '#e5e7eb',
                background: days === opt.value ? '#dbeafe' : '#fff',
                color: days === opt.value ? '#1d4ed8' : '#6b7280',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <Empty message="carregando tendência óptica…" />}
      {error && <Empty message={`Erro: ${String(error.message || error)}`} />}
      {!isLoading && !error && !points.length && (
        <Empty message="sem histórico óptico (ONU offline, ou poller-optical ainda não cobriu)" />
      )}
      {!isLoading && !error && points.length > 0 && chart && (
        <svg
          width={W}
          height={H}
          role="img"
          aria-label={`Tendência óptica ${sn} últimos ${days} dias`}
          data-testid="optical-sparkline-svg"
          style={{ display: 'block', background: '#fafafa', borderRadius: 4 }}
        >
          {/* Eixos / ticks Y */}
          {chart.yTicks.map((t, i) => (
            <g key={`yt-${i}`}>
              <line
                x1={PAD_L}
                x2={W - PAD_R}
                y1={t.y}
                y2={t.y}
                stroke="#e5e7eb"
                strokeWidth="1"
                strokeDasharray={i === 0 || i === 3 ? '0' : '2 2'}
              />
              <text x={PAD_L - 4} y={t.y + 3} fontSize="9" fill="#9ca3af" textAnchor="end">
                {t.v}
              </text>
            </g>
          ))}

          {/* Threshold marginal -25 dBm */}
          {chart.yThreshold != null && (
            <g>
              <line
                x1={PAD_L}
                x2={W - PAD_R}
                y1={chart.yThreshold}
                y2={chart.yThreshold}
                stroke="#dc2626"
                strokeWidth="1"
                strokeDasharray="3 3"
                opacity="0.55"
              />
              <text
                x={W - PAD_R - 2}
                y={chart.yThreshold - 2}
                fontSize="9"
                fill="#dc2626"
                textAnchor="end"
                opacity="0.7"
              >
                -25 (marginal)
              </text>
            </g>
          )}

          {/* Eixo X — labels esquerda/direita */}
          <text x={PAD_L} y={H - 6} fontSize="9" fill="#9ca3af">
            {new Date(chart.tMin).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
          </text>
          <text x={W - PAD_R} y={H - 6} fontSize="9" fill="#9ca3af" textAnchor="end">
            {new Date(chart.tMax).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
          </text>

          {/* Linha rx (verde/amarelo/vermelho conforme valor atual) */}
          {chart.rxPath && (
            <path
              d={chart.rxPath}
              fill="none"
              stroke={rxColor(stats?.rxLast)}
              strokeWidth="1.5"
              data-testid="optical-rx-line"
            />
          )}
          {/* Linha tx — sempre cinza-azul, secundária */}
          {chart.txPath && (
            <path
              d={chart.txPath}
              fill="none"
              stroke="#3b82f6"
              strokeWidth="1"
              opacity="0.55"
              strokeDasharray="2 2"
              data-testid="optical-tx-line"
            />
          )}
        </svg>
      )}
    </div>
  );
}

export default OpticalSparkline;
